import {
  Box,
  Button,
  Flex,
  Icon,
  Heading,
  Input,
  Spinner,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import Card from "components/card/Card.js";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MdUpload } from "react-icons/md";
import Dropzone from "views/admin/profile/components/Dropzone";
import useSystem from "providers/System";
import constants from "providers/Constants";
import { NavLink } from "react-router-dom";

export default function Upload(props) {
  const { t } = useTranslation();
  const system = useSystem();
  const [uploads, setUploads] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [pendingFile, setPendingFile] = React.useState(null);
  const [pendingBase64, setPendingBase64] = React.useState(null);
  const [fileName, setFileName] = React.useState("");
  const { used, onChange, total, ...rest } = props;

  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const brandColor = useColorModeValue("brand.500", "white");
  const textColorSecondary = "gray.400";
  const cardShadow = useColorModeValue(
    "0px 18px 40px rgba(112, 144, 176, 0.12)",
    "unset"
  );

  useEffect(() => { }, [uploads]);

  return (
    <Card
      {...rest}
      mb={{ base: "0px", "2xl": "20px" }}
      boxShadow={cardShadow}
      p='20px'
      overflow="hidden"
    >
      <Flex
        w='100%'
        direction={{ base: "column", "2xl": "row" }}
        align='stretch'
        gap={{ base: 6, "2xl": 10 }}
        overflow="hidden"
      >
        <Dropzone
          w={{ base: "100%", "2xl": "280px" }}
          flexShrink={0}
          onDrop={(acceptedFiles) => {
            const file = acceptedFiles?.[0];
            if (!file) return;
            setPendingFile(file);
            setFileName(file.name || "");
            const reader = new FileReader();
            reader.onload = () => {
              const base64String = reader.result;
              setPendingBase64(base64String);
            };
            reader.readAsDataURL(file);
          }}
          maxH='260px'
          minH='220px'
          content={
            <Box>
              <Icon as={MdUpload} w='80px' h='80px' color={brandColor} />
              <Flex justify='center' mx='auto' mb='12px'>
                <Text fontSize='xl' fontWeight='700' color={brandColor}>
                  {t('Upload File')}
                </Text>
              </Flex>
              <Text fontSize='sm' fontWeight='500' color='secondaryGray.500'>
                {t('Only .PNG, .JPG and .JPEG files are allowed')}
              </Text>
            </Box>
          }
        />
        <Flex direction='column' flex='1' minW={0} pe={{ base: 0, "2xl": '44px' }} overflow="hidden">
          <Heading
            size='md'
            color={textColorPrimary}
            textAlign='start'
            mt={{ base: "20px", "2xl": "50px" }}>
            {t('File Uploader')}
          </Heading>
          <Text
            color={textColorSecondary}
            fontSize='sm'
            my={{ base: "auto", "2xl": "10px" }}
            mx='0px'
            textAlign='start'
            wordBreak="break-word"
            overflow="hidden">
            {t('This tool allows you upload files, identity documents, images and others from your device to BlockMap.')}
          </Text>
          {loading && (
            <Flex justify='center' align='center' mt='30px'>
              <Spinner size='xl' />
            </Flex>
          )}
          {!loading && pendingBase64 && (
            <Flex direction={{ base: 'column', md: 'row' }} gap={3} mt='20px' align='flex-start'>
              <Input
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder={t('Enter file name')}
                maxW={{ base: '100%', md: '320px' }}
              />
              <Button
                onClick={() => uploadDocument(null, pendingBase64, fileName || pendingFile?.name)}
                isDisabled={!pendingBase64}
                isLoading={loading}
                variant='brand'
                fontSize='sm'
                py={2}
                px={4}
              >
                <span style={{ fontSize: '0.95em' }}>{t('Upload')}</span>
              </Button>
            </Flex>
          )}
          {!loading && uploads.length > 0 && (
            uploads.map((entry, i) => (
              <Flex w='100%' direction='column' mb='50px' key={i}>
                <Text
                  color={'red'}
                  fontSize='md'
                  mt={{ base: "20px", "2xl": "auto" }}
                  textAlign='start'>
                  * {t('File Name')}: {entry?.name}
                </Text>
                <NavLink target='_blank' to={entry?.value}>
                  <Button
                    me='100%'
                    mb='50px'
                    w='140px'
                    minW='140px'
                    mt={{ base: "20px", "2xl": "auto" }}
                    variant='brand'
                    fontWeight='500'>
                    {t('View File')}
                  </Button>
                </NavLink>
              </Flex>
            ))
          )}
        </Flex>
      </Flex>
    </Card>
  );

  function uploadDocument(user_id, base64String, nameOverride) {
    setLoading(true);
    system.uploadMedia(user_id, base64String, (error, data) => {
      setLoading(false);
      if (error) {
        return constants.showToast('error', 'FAILED', error.message);
      }
      const result = {
        name: nameOverride || Date.now(),
        value: data.uri
      };
      setUploads([result]);
      onChange(result);
      // reset pending state
      setPendingBase64(null);
      setPendingFile(null);
      setFileName("");
    });
  }
}
